/**
 * Position → role inference. Maps a world `{x, z}` coordinate to the
 * canonical role tag that best fits where the token lands — FIFA /
 * Football Manager tactics-screen idiom where dragging a CDM to a
 * striker zone re-tags them ST.
 *
 * Convention: HOME attacks toward `-z` (per `formationProjection.ts`
 * + the rest of the codebase). For away, the helper internally
 * mirrors z so coaches can drag away tokens with the same intuition.
 *
 * Zones are tuned to a 105×68 m pitch. The mapping is opinionated:
 *   - Goalkeeper:        deep central
 *   - Centre backs:      back third central
 *   - Full-backs:        back third wide
 *   - Wing-backs:        mid/back wide (between FB and W)
 *   - DM / CDM:          base of midfield central
 *   - CM:                heart of midfield central
 *   - LM / RM:           midfield wide
 *   - CAM / AM:          top of midfield central
 *   - LW / RW:           attacking third wide
 *   - ST / CF:           attacking third central
 *
 * The function is deterministic + pure — same coordinates always
 * yield the same role regardless of formation context. A future
 * iteration could weight the inference toward the player's CURRENT
 * formation (so dragging a 4-3-3 winger inward gives IF/IW instead
 * of CAM) but the zone-table baseline is a clean v1.
 */

const FIELD_LENGTH_M = 105;
const FIELD_WIDTH_M = 68;
const HALF_L = FIELD_LENGTH_M / 2;
const HALF_W = FIELD_WIDTH_M / 2;

/** One zone in normalised pitch space.
 *   xNorm ∈ [0, 1] runs left → right (0 = LEFT sideline)
 *   zNorm ∈ [0, 1] runs attacking → own (0 = attacking goal end,
 *                                         1 = own goal end)
 *  for the HOME view. */
interface PositionZone {
  role: string;
  xMin: number;
  xMax: number;
  zMin: number;
  zMax: number;
}

// Order matters when a point falls into overlapping zones — the
// FIRST match wins. Specific zones (GK, central-back) come before
// broader fallbacks.
const ZONES: PositionZone[] = [
  // Goalkeeper — deep central. Narrow x band to avoid eating a CB.
  { role: 'GK', xMin: 0.4, xMax: 0.6, zMin: 0.9, zMax: 1.0 },

  // Back four — defensive third
  { role: 'LB', xMin: 0.0, xMax: 0.2, zMin: 0.7, zMax: 0.92 },
  { role: 'RB', xMin: 0.8, xMax: 1.0, zMin: 0.7, zMax: 0.92 },
  { role: 'CB', xMin: 0.2, xMax: 0.8, zMin: 0.7, zMax: 0.9 },

  // Wing-backs — sit between full-backs and wingers vertically
  { role: 'LWB', xMin: 0.0, xMax: 0.2, zMin: 0.5, zMax: 0.7 },
  { role: 'RWB', xMin: 0.8, xMax: 1.0, zMin: 0.5, zMax: 0.7 },

  // Defensive midfield
  { role: 'CDM', xMin: 0.3, xMax: 0.7, zMin: 0.55, zMax: 0.72 },

  // Central midfield
  { role: 'CM', xMin: 0.3, xMax: 0.7, zMin: 0.4, zMax: 0.58 },

  // Wide midfield
  { role: 'LM', xMin: 0.0, xMax: 0.25, zMin: 0.35, zMax: 0.55 },
  { role: 'RM', xMin: 0.75, xMax: 1.0, zMin: 0.35, zMax: 0.55 },

  // Attacking midfield / no.10
  { role: 'CAM', xMin: 0.3, xMax: 0.7, zMin: 0.22, zMax: 0.42 },

  // Wingers — attacking third wide
  { role: 'LW', xMin: 0.0, xMax: 0.25, zMin: 0.05, zMax: 0.38 },
  { role: 'RW', xMin: 0.75, xMax: 1.0, zMin: 0.05, zMax: 0.38 },

  // Striker / centre-forward — attacking third central
  { role: 'ST', xMin: 0.3, xMax: 0.7, zMin: 0.0, zMax: 0.25 },
];

/** Distance from a point to the centre of a zone — used as the
 *  fallback when no zone hard-matches. */
function distanceToZoneCentre(
  xNorm: number,
  zNorm: number,
  zone: PositionZone,
): number {
  const cx = (zone.xMin + zone.xMax) / 2;
  const cz = (zone.zMin + zone.zMax) / 2;
  const dx = xNorm - cx;
  const dz = zNorm - cz;
  return Math.sqrt(dx * dx + dz * dz);
}

/**
 * Infer the canonical role tag for a world `{x, z}` position on a
 * standard 105×68 pitch. `side` controls the attacking direction —
 * home attacks -z, away attacks +z. The away view is mirrored
 * internally so the zone table stays single-source.
 */
export function inferRoleAtPosition(
  x: number,
  z: number,
  side: 'home' | 'away',
): string {
  // Normalise to [0, 1] in the HOME convention. For away, mirror BOTH
  // axes so 'forward' is always toward z=0 here AND the team's own
  // left side maps to xNorm < 0.5. Without the x mirror, an away RB
  // at world (-23, +28) would infer as "LB" — wrong from the away
  // team's own POV.
  const rawXNorm = (x + HALF_W) / FIELD_WIDTH_M;
  const rawZNorm = (z + HALF_L) / FIELD_LENGTH_M;
  const xNorm = side === 'away' ? 1 - rawXNorm : rawXNorm;
  const zNorm = side === 'away' ? 1 - rawZNorm : rawZNorm;

  // First-match hard zone. Iteration order encodes priority.
  for (const zone of ZONES) {
    if (
      xNorm >= zone.xMin &&
      xNorm <= zone.xMax &&
      zNorm >= zone.zMin &&
      zNorm <= zone.zMax
    ) {
      return zone.role;
    }
  }

  // Fallback — pick the zone whose centre is nearest. Keeps every
  // pitch-position resolvable; coach won't see an "unknown" role.
  let bestRole = ZONES[0]!.role;
  let bestDist = Infinity;
  for (const zone of ZONES) {
    const d = distanceToZoneCentre(xNorm, zNorm, zone);
    if (d < bestDist) {
      bestDist = d;
      bestRole = zone.role;
    }
  }
  return bestRole;
}
